import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import AndroidAdUnitTrial from './AndroidAdUnitTrial';

interface AdConfig {
  _id?: string;
  enabled: boolean;
  androidAppId: string;
  iosAppId: string;
  androidInterstitialAdId: string;
  iosInterstitialAdId: string;
  androidRewardedAdId: string;
  iosRewardedAdId: string;
  androidSplashInterstitialAdId: string;
  iosSplashInterstitialAdId: string;
  androidSplashRewardedAdId: string;
  iosSplashRewardedAdId: string;
  androidSwapInterstitialAdId: string;
  iosSwapInterstitialAdId: string;
  androidSwapRewardedAdId: string;
  iosSwapRewardedAdId: string;
  androidPremiumTemplateInterstitialAdId: string;
  iosPremiumTemplateInterstitialAdId: string;
  androidPremiumTemplateRewardedAdId: string;
  iosPremiumTemplateRewardedAdId: string;
  androidWatermarkRemovalInterstitialAdId: string;
  iosWatermarkRemovalInterstitialAdId: string;
  androidWatermarkRemovalRewardedAdId: string;
  iosWatermarkRemovalRewardedAdId: string;
  swapAdType: 'interstitial' | 'rewarded';
  splashAdType: 'none' | 'interstitial' | 'rewarded';
  premiumTemplateAdType: 'interstitial' | 'rewarded';
  watermarkRemovalAdType: 'interstitial' | 'rewarded';
  creditEarnAdType: 'interstitial' | 'rewarded';
  creditEarnCooldownSeconds: number;
  androidCreditEarnInterstitialAdId: string;
  iosCreditEarnInterstitialAdId: string;
  androidCreditEarnRewardedAdId: string;
  iosCreditEarnRewardedAdId: string;
  v2AndroidAppId: string;
  v2IosAppId: string;
  v2AndroidInterstitialAdId: string;
  v2IosInterstitialAdId: string;
  v2AndroidRewardedAdId: string;
  v2IosRewardedAdId: string;
  v2TestMode: boolean;
  yandexEnabled: boolean;
  yandexTestMode: boolean;
  yandexAndroidAppId: string;
  yandexIosAppId: string;
  yandexAndroidInterstitialAdId: string;
  yandexIosInterstitialAdId: string;
  yandexAndroidRewardedAdId: string;
  yandexIosRewardedAdId: string;
  androidAdUnitTrialEnabled: boolean;
  androidAdUnitTrialPercentage: number;
  androidAdUnitTrialInterstitialAdId: string;
  androidAdUnitTrialRewardedAdId: string;
  testMode: boolean;
  lastUpdatedBy?: {
    name: string;
    email: string;
  };
  updatedAt?: string;
}

const AdConfiguration: React.FC = () => {
  const [formData, setFormData] = useState<AdConfig>({
    enabled: false,
    androidAppId: '',
    iosAppId: '',
    androidInterstitialAdId: '',
    iosInterstitialAdId: '',
    androidRewardedAdId: '',
    iosRewardedAdId: '',
    androidSplashInterstitialAdId: '',
    iosSplashInterstitialAdId: '',
    androidSplashRewardedAdId: '',
    iosSplashRewardedAdId: '',
    androidSwapInterstitialAdId: '',
    iosSwapInterstitialAdId: '',
    androidSwapRewardedAdId: '',
    iosSwapRewardedAdId: '',
    androidPremiumTemplateInterstitialAdId: '',
    iosPremiumTemplateInterstitialAdId: '',
    androidPremiumTemplateRewardedAdId: '',
    iosPremiumTemplateRewardedAdId: '',
    androidWatermarkRemovalInterstitialAdId: '',
    iosWatermarkRemovalInterstitialAdId: '',
    androidWatermarkRemovalRewardedAdId: '',
    iosWatermarkRemovalRewardedAdId: '',
    swapAdType: 'interstitial',
    splashAdType: 'none',
    premiumTemplateAdType: 'interstitial',
    watermarkRemovalAdType: 'interstitial',
    creditEarnAdType: 'rewarded',
    creditEarnCooldownSeconds: 15,
    androidCreditEarnInterstitialAdId: '',
    iosCreditEarnInterstitialAdId: '',
    androidCreditEarnRewardedAdId: '',
    iosCreditEarnRewardedAdId: '',
    v2AndroidAppId: '',
    v2IosAppId: '',
    v2AndroidInterstitialAdId: '',
    v2IosInterstitialAdId: '',
    v2AndroidRewardedAdId: '',
    v2IosRewardedAdId: '',
    v2TestMode: false,
    yandexEnabled: false,
    yandexTestMode: false,
    yandexAndroidAppId: '19047306',
    yandexIosAppId: '19047313',
    yandexAndroidInterstitialAdId: 'R-M-19047306-1',
    yandexIosInterstitialAdId: 'R-M-19047313-1',
    yandexAndroidRewardedAdId: 'R-M-19047306-2',
    yandexIosRewardedAdId: 'R-M-19047313-2',
    androidAdUnitTrialEnabled: false,
    androidAdUnitTrialPercentage: 0,
    androidAdUnitTrialInterstitialAdId: '',
    androidAdUnitTrialRewardedAdId: '',
    testMode: false,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const queryClient = useQueryClient();

  // Fetch ad config
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['adConfig'],
    queryFn: async () => {
      const response = await apiService.getAdConfig();
      return response.data as AdConfig;
    }
  });

  // Update form data when data is fetched
  useEffect(() => {
    if (data) {
      setFormData({
        enabled: data.enabled ?? false,
        androidAppId: data.androidAppId ?? '',
        iosAppId: data.iosAppId ?? '',
        androidInterstitialAdId: data.androidInterstitialAdId ?? '',
        iosInterstitialAdId: data.iosInterstitialAdId ?? '',
        androidRewardedAdId: data.androidRewardedAdId ?? '',
        iosRewardedAdId: data.iosRewardedAdId ?? '',
        androidSplashInterstitialAdId: data.androidSplashInterstitialAdId ?? '',
        iosSplashInterstitialAdId: data.iosSplashInterstitialAdId ?? '',
        androidSplashRewardedAdId: data.androidSplashRewardedAdId ?? '',
        iosSplashRewardedAdId: data.iosSplashRewardedAdId ?? '',
        androidSwapInterstitialAdId: data.androidSwapInterstitialAdId ?? '',
        iosSwapInterstitialAdId: data.iosSwapInterstitialAdId ?? '',
        androidSwapRewardedAdId: data.androidSwapRewardedAdId ?? '',
        iosSwapRewardedAdId: data.iosSwapRewardedAdId ?? '',
        androidPremiumTemplateInterstitialAdId: data.androidPremiumTemplateInterstitialAdId ?? '',
        iosPremiumTemplateInterstitialAdId: data.iosPremiumTemplateInterstitialAdId ?? '',
        androidPremiumTemplateRewardedAdId: data.androidPremiumTemplateRewardedAdId ?? '',
        iosPremiumTemplateRewardedAdId: data.iosPremiumTemplateRewardedAdId ?? '',
        androidWatermarkRemovalInterstitialAdId: data.androidWatermarkRemovalInterstitialAdId ?? '',
        iosWatermarkRemovalInterstitialAdId: data.iosWatermarkRemovalInterstitialAdId ?? '',
        androidWatermarkRemovalRewardedAdId: data.androidWatermarkRemovalRewardedAdId ?? '',
        iosWatermarkRemovalRewardedAdId: data.iosWatermarkRemovalRewardedAdId ?? '',
        swapAdType: data.swapAdType ?? 'interstitial',
        splashAdType: data.splashAdType ?? 'none',
        premiumTemplateAdType: data.premiumTemplateAdType ?? 'interstitial',
        watermarkRemovalAdType: data.watermarkRemovalAdType ?? 'interstitial',
        creditEarnAdType: data.creditEarnAdType ?? 'rewarded',
        creditEarnCooldownSeconds: data.creditEarnCooldownSeconds ?? 15,
        androidCreditEarnInterstitialAdId: data.androidCreditEarnInterstitialAdId ?? '',
        iosCreditEarnInterstitialAdId: data.iosCreditEarnInterstitialAdId ?? '',
        androidCreditEarnRewardedAdId: data.androidCreditEarnRewardedAdId ?? '',
        iosCreditEarnRewardedAdId: data.iosCreditEarnRewardedAdId ?? '',
        v2AndroidAppId: data.v2AndroidAppId ?? '',
        v2IosAppId: data.v2IosAppId ?? '',
        v2AndroidInterstitialAdId: data.v2AndroidInterstitialAdId ?? '',
        v2IosInterstitialAdId: data.v2IosInterstitialAdId ?? '',
        v2AndroidRewardedAdId: data.v2AndroidRewardedAdId ?? '',
        v2IosRewardedAdId: data.v2IosRewardedAdId ?? '',
        v2TestMode: data.v2TestMode ?? false,
        yandexEnabled: data.yandexEnabled ?? false,
        yandexTestMode: data.yandexTestMode ?? false,
        yandexAndroidAppId: data.yandexAndroidAppId ?? '19047306',
        yandexIosAppId: data.yandexIosAppId ?? '19047313',
        yandexAndroidInterstitialAdId: data.yandexAndroidInterstitialAdId ?? 'R-M-19047306-1',
        yandexIosInterstitialAdId: data.yandexIosInterstitialAdId ?? 'R-M-19047313-1',
        yandexAndroidRewardedAdId: data.yandexAndroidRewardedAdId ?? 'R-M-19047306-2',
        yandexIosRewardedAdId: data.yandexIosRewardedAdId ?? 'R-M-19047313-2',
        androidAdUnitTrialEnabled: data.androidAdUnitTrialEnabled ?? false,
        androidAdUnitTrialPercentage: data.androidAdUnitTrialPercentage ?? 0,
        androidAdUnitTrialInterstitialAdId: data.androidAdUnitTrialInterstitialAdId ?? '',
        androidAdUnitTrialRewardedAdId: data.androidAdUnitTrialRewardedAdId ?? '',
        testMode: data.testMode ?? false,
        _id: data._id,
        lastUpdatedBy: data.lastUpdatedBy,
        updatedAt: data.updatedAt,
      });
    }
  }, [data]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (config: AdConfig) => {
      const response = await apiService.updateAdConfig(config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adConfig'] });
      setSnackbar({
        open: true,
        message: 'Ad configuration updated successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update ad configuration',
        severity: 'error'
      });
    }
  });

  // Toggle enabled mutation
  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleAdsEnabled(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adConfig'] });
      setSnackbar({
        open: true,
        message: `Ads ${formData.enabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle ads',
        severity: 'error'
      });
    }
  });

  const handleInputChange = (field: keyof AdConfig, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  // Same rule as the app's AdConfig.hasV2: v2 units win when both v2 interstitials are set.
  const usesV2 = formData.v2AndroidInterstitialAdId !== '' && formData.v2IosInterstitialAdId !== '';

  const handleToggleEnabled = () => {
    toggleMutation.mutate(!formData.enabled);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load ad configuration. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Ad Configuration
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Enable/Disable Toggle */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" gutterBottom>
                  Enable Ads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Toggle ads on or off for the entire application
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={handleToggleEnabled}
                    color="primary"
                  />
                }
                label={formData.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
          </Paper>

          {formData.enabled && (
            <>
              <Divider sx={{ my: 3 }} />

              {/* App IDs */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                App IDs
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Android App ID"
                    value={formData.androidAppId}
                    onChange={(e) => handleInputChange('androidAppId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX~XXXXXXXXXX"
                    helperText="Your Google AdMob Android App ID"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="iOS App ID"
                    value={formData.iosAppId}
                    onChange={(e) => handleInputChange('iosAppId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX~XXXXXXXXXX"
                    helperText="Your Google AdMob iOS App ID"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Shared Ad Unit IDs - Used by Per-Feature Configuration */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📌 Important: Shared Ad Units
                </Typography>
                <Typography variant="body2">
                  These shared ad unit IDs are used by the new per-feature configuration system. 
                  Each feature (Photo Swap, Group Swap, Video Swap, etc.) will use these shared ad units 
                  based on the ad type selected in Feature Configuration settings.
                </Typography>
              </Alert>

              <Typography variant="h6" gutterBottom>
                Shared Ad Unit IDs (For Per-Feature Configuration)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure the shared ad unit IDs that will be used across all features. 
                These are required for the new per-feature ad configuration system.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Android Shared Interstitial Ad ID"
                    value={formData.androidInterstitialAdId || ''}
                    onChange={(e) => handleInputChange('androidInterstitialAdId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                    helperText="Required for shared interstitial ads (used by all features)"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="iOS Shared Interstitial Ad ID"
                    value={formData.iosInterstitialAdId || ''}
                    onChange={(e) => handleInputChange('iosInterstitialAdId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                    helperText="Required for shared interstitial ads (used by all features)"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Android Shared Rewarded Ad ID"
                    value={formData.androidRewardedAdId || ''}
                    onChange={(e) => handleInputChange('androidRewardedAdId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                    helperText="Required for shared rewarded ads (used by all features)"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="iOS Shared Rewarded Ad ID"
                    value={formData.iosRewardedAdId || ''}
                    onChange={(e) => handleInputChange('iosRewardedAdId', e.target.value)}
                    placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                    helperText="Required for shared rewarded ads (used by all features)"
                    required
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Splash Screen Ad Type */}
              <Typography variant="h6" gutterBottom>
                Ad After Splash Screen
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ad Type After Splash</InputLabel>
                    <Select
                      value={formData.splashAdType}
                      onChange={(e) => handleInputChange('splashAdType', e.target.value)}
                      label="Ad Type After Splash"
                    >
                      <MenuItem value="none">No Ad</MenuItem>
                      <MenuItem value="interstitial">Interstitial Ad (with close button)</MenuItem>
                      <MenuItem value="rewarded">Rewarded Ad (must watch to completion)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose which type of ad to show after the splash screen. Interstitial ads can be closed, while rewarded ads must be watched to completion.
                  </Typography>
                </Grid>
              </Grid>

              {/* Splash Screen Ad Unit IDs */}
              {formData.splashAdType !== 'none' && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                    Splash Screen Ad Unit IDs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure specific ad unit IDs for splash screen.
                  </Typography>
                  {formData.splashAdType === 'interstitial' ? (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Android Splash Interstitial Ad ID"
                          value={formData.androidSplashInterstitialAdId || ''}
                          onChange={(e) => handleInputChange('androidSplashInterstitialAdId', e.target.value)}
                          placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                          helperText="Required for splash screen interstitial ads"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="iOS Splash Interstitial Ad ID"
                          value={formData.iosSplashInterstitialAdId || ''}
                          onChange={(e) => handleInputChange('iosSplashInterstitialAdId', e.target.value)}
                          placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                          helperText="Required for splash screen interstitial ads"
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Android Splash Rewarded Ad ID"
                          value={formData.androidSplashRewardedAdId || ''}
                          onChange={(e) => handleInputChange('androidSplashRewardedAdId', e.target.value)}
                          placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                          helperText="Required for splash screen rewarded ads"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="iOS Splash Rewarded Ad ID"
                          value={formData.iosSplashRewardedAdId || ''}
                          onChange={(e) => handleInputChange('iosSplashRewardedAdId', e.target.value)}
                          placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                          helperText="Required for splash screen rewarded ads"
                        />
                      </Grid>
                    </Grid>
                  )}
                </>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Ad Type Selection for Swap */}
              <Typography variant="h6" gutterBottom>
                Ad Type When Swapping
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ad Type</InputLabel>
                    <Select
                      value={formData.swapAdType}
                      onChange={(e) => handleInputChange('swapAdType', e.target.value)}
                      label="Ad Type"
                    >
                      <MenuItem value="interstitial">Interstitial Ad (with close button)</MenuItem>
                      <MenuItem value="rewarded">Rewarded Ad (must watch to completion)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose which type of ad to show when user starts swapping. Ads will be shown on every swap. Interstitial ads can be closed, while rewarded ads must be watched to completion.
                  </Typography>
                </Grid>
              </Grid>

              {/* Swap Screen Ad Unit IDs */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Swap Screen Ad Unit IDs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure specific ad unit IDs for swap screen.
              </Typography>
              {formData.swapAdType === 'interstitial' ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Swap Interstitial Ad ID"
                      value={formData.androidSwapInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('androidSwapInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for swap screen interstitial ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Swap Interstitial Ad ID"
                      value={formData.iosSwapInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('iosSwapInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for swap screen interstitial ads"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Swap Rewarded Ad ID"
                      value={formData.androidSwapRewardedAdId || ''}
                      onChange={(e) => handleInputChange('androidSwapRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for swap screen rewarded ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Swap Rewarded Ad ID"
                      value={formData.iosSwapRewardedAdId || ''}
                      onChange={(e) => handleInputChange('iosSwapRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for swap screen rewarded ads"
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Premium Template Ad Type */}
              <Typography variant="h6" gutterBottom>
                Ad Type When Using Premium Template
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ad Type</InputLabel>
                    <Select
                      value={formData.premiumTemplateAdType}
                      onChange={(e) => handleInputChange('premiumTemplateAdType', e.target.value)}
                      label="Ad Type"
                    >
                      <MenuItem value="interstitial">Interstitial Ad (with close button)</MenuItem>
                      <MenuItem value="rewarded">Rewarded Ad (must watch to completion)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose which type of ad to show when user uses a premium template.
                  </Typography>
                </Grid>
              </Grid>

              {/* Premium Template Ad Unit IDs */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Premium Template Ad Unit IDs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure specific ad unit IDs for premium template usage.
              </Typography>
              {formData.premiumTemplateAdType === 'interstitial' ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Premium Template Interstitial Ad ID"
                      value={formData.androidPremiumTemplateInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('androidPremiumTemplateInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for premium template interstitial ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Premium Template Interstitial Ad ID"
                      value={formData.iosPremiumTemplateInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('iosPremiumTemplateInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for premium template interstitial ads"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Premium Template Rewarded Ad ID"
                      value={formData.androidPremiumTemplateRewardedAdId || ''}
                      onChange={(e) => handleInputChange('androidPremiumTemplateRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for premium template rewarded ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Premium Template Rewarded Ad ID"
                      value={formData.iosPremiumTemplateRewardedAdId || ''}
                      onChange={(e) => handleInputChange('iosPremiumTemplateRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for premium template rewarded ads"
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Watermark Removal Ad Type */}
              <Typography variant="h6" gutterBottom>
                Ad Type When Removing Watermark
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ad Type</InputLabel>
                    <Select
                      value={formData.watermarkRemovalAdType}
                      onChange={(e) => handleInputChange('watermarkRemovalAdType', e.target.value)}
                      label="Ad Type"
                    >
                      <MenuItem value="interstitial">Interstitial Ad (with close button)</MenuItem>
                      <MenuItem value="rewarded">Rewarded Ad (must watch to completion)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose which type of ad to show when user removes watermark.
                  </Typography>
                </Grid>
              </Grid>

              {/* Watermark Removal Ad Unit IDs */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Watermark Removal Ad Unit IDs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure specific ad unit IDs for watermark removal.
              </Typography>
              {formData.watermarkRemovalAdType === 'interstitial' ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Watermark Removal Interstitial Ad ID"
                      value={formData.androidWatermarkRemovalInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('androidWatermarkRemovalInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for watermark removal interstitial ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Watermark Removal Interstitial Ad ID"
                      value={formData.iosWatermarkRemovalInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('iosWatermarkRemovalInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for watermark removal interstitial ads"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Watermark Removal Rewarded Ad ID"
                      value={formData.androidWatermarkRemovalRewardedAdId || ''}
                      onChange={(e) => handleInputChange('androidWatermarkRemovalRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for watermark removal rewarded ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Watermark Removal Rewarded Ad ID"
                      value={formData.iosWatermarkRemovalRewardedAdId || ''}
                      onChange={(e) => handleInputChange('iosWatermarkRemovalRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for watermark removal rewarded ads"
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Credit Earn Ad Configuration */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Credit Earn Ad Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure ads shown when users watch ads to earn credits in the More tab
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ad Type</InputLabel>
                    <Select
                      value={formData.creditEarnAdType}
                      onChange={(e) => handleInputChange('creditEarnAdType', e.target.value)}
                      label="Ad Type"
                    >
                      <MenuItem value="interstitial">Interstitial Ad (with close button)</MenuItem>
                      <MenuItem value="rewarded">Rewarded Ad (must watch to completion)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cooldown Between Ads (seconds)"
                    value={formData.creditEarnCooldownSeconds}
                    onChange={(e) => handleInputChange('creditEarnCooldownSeconds', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 3600 }}
                    helperText="Time user must wait before watching another ad (0 = no cooldown, max 3600 seconds)"
                  />
                </Grid>
              </Grid>

              {formData.creditEarnAdType === 'interstitial' ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Credit Earn Interstitial Ad ID"
                      value={formData.androidCreditEarnInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('androidCreditEarnInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for credit earn interstitial ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Credit Earn Interstitial Ad ID"
                      value={formData.iosCreditEarnInterstitialAdId || ''}
                      onChange={(e) => handleInputChange('iosCreditEarnInterstitialAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for credit earn interstitial ads"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Android Credit Earn Rewarded Ad ID"
                      value={formData.androidCreditEarnRewardedAdId || ''}
                      onChange={(e) => handleInputChange('androidCreditEarnRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for credit earn rewarded ads"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="iOS Credit Earn Rewarded Ad ID"
                      value={formData.iosCreditEarnRewardedAdId || ''}
                      onChange={(e) => handleInputChange('iosCreditEarnRewardedAdId', e.target.value)}
                      placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                      helperText="Required for credit earn rewarded ads"
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Test Mode */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.testMode}
                    onChange={(e) => handleInputChange('testMode', e.target.checked)}
                  />
                }
                label="Test Mode (Use Google Test Ad IDs)"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                When enabled, the app will use Google's test ad IDs instead of your production ad IDs
              </Typography>
            </>
          )}

          {/* Last Updated Info */}
          {/* ── Yandex Ads ── */}
          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'warning.main', bgcolor: 'background.default' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" color="warning.main">Yandex Mobile Ads</Typography>
                <Typography variant="body2" color="text.secondary">Runs alongside AdMob as a separate network</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.yandexEnabled}
                    onChange={(e) => handleInputChange('yandexEnabled', e.target.checked)}
                    color="warning"
                  />
                }
                label={formData.yandexEnabled ? 'Enabled' : 'Disabled'}
              />
            </Box>

            {formData.yandexEnabled && (
              <>
                <Box display="flex" alignItems="center" mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.yandexTestMode}
                        onChange={(e) => handleInputChange('yandexTestMode', e.target.checked)}
                        color="error"
                      />
                    }
                    label={<Typography color={formData.yandexTestMode ? 'error' : 'text.secondary'}>Test Mode (uses demo ad unit IDs)</Typography>}
                  />
                </Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>App IDs</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Android App ID" value={formData.yandexAndroidAppId}
                      onChange={(e) => handleInputChange('yandexAndroidAppId', e.target.value)} helperText="Yandex Android App ID" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="iOS App ID" value={formData.yandexIosAppId}
                      onChange={(e) => handleInputChange('yandexIosAppId', e.target.value)} helperText="Yandex iOS App ID" />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>Interstitial Ad Unit</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Android Interstitial" value={formData.yandexAndroidInterstitialAdId}
                      onChange={(e) => handleInputChange('yandexAndroidInterstitialAdId', e.target.value)} placeholder="R-M-XXXXXXXX-1" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="iOS Interstitial" value={formData.yandexIosInterstitialAdId}
                      onChange={(e) => handleInputChange('yandexIosInterstitialAdId', e.target.value)} placeholder="R-M-XXXXXXXX-1" />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>Rewarded Ad Unit</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Android Rewarded" value={formData.yandexAndroidRewardedAdId}
                      onChange={(e) => handleInputChange('yandexAndroidRewardedAdId', e.target.value)} placeholder="R-M-XXXXXXXX-2" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="iOS Rewarded" value={formData.yandexIosRewardedAdId}
                      onChange={(e) => handleInputChange('yandexIosRewardedAdId', e.target.value)} placeholder="R-M-XXXXXXXX-2" />
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          {/* ── New AdMob Account (v2) ── */}
          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2, mb: 3, border: '2px solid', borderColor: 'primary.main', bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom color="primary">
              New AdMob Account — v2 (New App Version Only)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These settings are used exclusively by the new app version. Older versions are not affected.
              Configure just one interstitial and one rewarded ad unit — they are reused across all actions.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.v2TestMode}
                  onChange={(e) => handleInputChange('v2TestMode', e.target.checked)}
                  color="warning"
                />
              }
              label={<Typography variant="body2" color="warning.main" fontWeight="bold">{formData.v2TestMode ? '🧪 Test Mode ON — using test ad IDs (v2 only)' : 'Test Mode OFF — using real ad IDs'}</Typography>}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mt: 2 }}>
              App IDs
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Android App ID (v2)"
                  value={formData.v2AndroidAppId}
                  onChange={(e) => handleInputChange('v2AndroidAppId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX~XXXXXXXXXX"
                  helperText="New AdMob Android App ID"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="iOS App ID (v2)"
                  value={formData.v2IosAppId}
                  onChange={(e) => handleInputChange('v2IosAppId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX~XXXXXXXXXX"
                  helperText="New AdMob iOS App ID"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Interstitial Ad Unit
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Android Interstitial Ad Unit (v2)"
                  value={formData.v2AndroidInterstitialAdId}
                  onChange={(e) => handleInputChange('v2AndroidInterstitialAdId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                  helperText="Used for all interstitial placements in new version"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="iOS Interstitial Ad Unit (v2)"
                  value={formData.v2IosInterstitialAdId}
                  onChange={(e) => handleInputChange('v2IosInterstitialAdId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                  helperText="Used for all interstitial placements in new version"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Rewarded Ad Unit
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Android Rewarded Ad Unit (v2)"
                  value={formData.v2AndroidRewardedAdId}
                  onChange={(e) => handleInputChange('v2AndroidRewardedAdId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                  helperText="Used for all rewarded placements in new version"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="iOS Rewarded Ad Unit (v2)"
                  value={formData.v2IosRewardedAdId}
                  onChange={(e) => handleInputChange('v2IosRewardedAdId', e.target.value)}
                  placeholder="ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                  helperText="Used for all rewarded placements in new version"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Android Ad Unit Trial ── */}
          <Divider sx={{ my: 3 }} />
          <AndroidAdUnitTrial
            enabled={formData.androidAdUnitTrialEnabled}
            percentage={formData.androidAdUnitTrialPercentage}
            interstitialAdId={formData.androidAdUnitTrialInterstitialAdId}
            rewardedAdId={formData.androidAdUnitTrialRewardedAdId}
            usesV2={usesV2}
            liveInterstitialAdId={usesV2 ? formData.v2AndroidInterstitialAdId : formData.androidInterstitialAdId}
            liveRewardedAdId={usesV2 ? formData.v2AndroidRewardedAdId : formData.androidRewardedAdId}
            testModeOn={usesV2 ? formData.v2TestMode : formData.testMode}
            adsEnabled={formData.enabled}
            onChange={handleInputChange}
          />

          {data?.lastUpdatedBy && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary">
                Last updated by: {data.lastUpdatedBy.name} ({data.lastUpdatedBy.email})
                {data.updatedAt && ` on ${new Date(data.updatedAt).toLocaleString()}`}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdConfiguration;

